<template>
    <section class="hpd" aria-labelledby="hpd-title">
        <h3 id="hpd-title" class="hpd__title">Display</h3>
        <fieldset class="hpd__group">
            <legend class="hpd__legend">Colours</legend>
            <div class="hpd__options">
                <label v-for="option in THEME_OPTIONS"
                       :key="option.value"
                       class="hpd__option"
                       :class="{'hpd__option_selected': theme === option.value}">
                    <input type="radio"
                           name="hpd_theme"
                           class="sr-only"
                           :value="option.value"
                           :tabindex="focusable ? 0 : -1"
                           v-model="theme"
                           @change="pick('theme', option.value)">
                    <i class="fas hpd__icon" :class="option.icon" aria-hidden="true"></i>
                    {{ option.label }}
                </label>
            </div>
        </fieldset>
        <fieldset class="hpd__group">
            <legend class="hpd__legend">Text size</legend>
            <div class="hpd__options">
                <label v-for="option in TEXT_SIZE_OPTIONS"
                       :key="option.value"
                       class="hpd__option"
                       :class="{'hpd__option_selected': textSize === option.value}">
                    <input type="radio"
                           name="hpd_text_size"
                           class="sr-only"
                           :value="option.value"
                           :tabindex="focusable ? 0 : -1"
                           v-model="textSize"
                           @change="pick('textSize', option.value)">
                    <span class="hpd__sample" :style="{fontSize: option.sample}" aria-hidden="true">Aa</span>
                    {{ option.label }}
                </label>
            </div>
        </fieldset>
    </section>
</template>

<script>
    /**
     * The theme and text size switch in the help panel, so it can be found from any page without
     * knowing where the settings are (docs/color-themes-plan.md §4.4). Always the viewer's own:
     * saved to their profile, or to a cookie when signed out.
     */
    import notifications from '~/vue/plugins/notifications';
    import {getDisplayPrefs, setDisplayPrefs} from 'shared/displayPrefs';
    import {THEME_OPTIONS, TEXT_SIZE_OPTIONS} from 'shared/displayOptions';
    import {GLOBAL_EVENTS, globalEventBus} from 'shared/globalEventBus';

    export default {
        name: 'HelpDeskDisplay',
        props: {
            // the panel is closed: keep the radios out of the tab order
            focusable: {type: Boolean, default: false},
        },
        data () {
            const {theme, textSize} = getDisplayPrefs();
            return {theme, textSize, THEME_OPTIONS, TEXT_SIZE_OPTIONS};
        },
        created () {
            // the settings page, or another tab's cookie on the next page, may change them too
            this.offDisplayChanged = globalEventBus.on(GLOBAL_EVENTS.DISPLAY_CHANGED, ({theme, textSize}) => {
                this.theme = theme;
                this.textSize = textSize;
            });
        },
        beforeUnmount () {
            this.offDisplayChanged();
        },
        methods: {
            pick (key, value) {
                setDisplayPrefs({[key]: value})
                    .then(() => notifications.success('Saved'), ({error, revert}) => {
                        Object.assign(this, revert);
                        notifications.error(error || 'Error');
                    });
            },
        },
    };
</script>

<style scoped lang="scss">
  .hpd {
    margin: 10px 25px 15px 0;
    color: var(--color-help-panel-on);

    &__title {
      margin-top: 5px;
      font-size: 1.5rem;
    }

    &__group {
      margin-top: 10px;
    }

    &__legend {
      border: 0;
      margin-bottom: 6px;
      color: inherit;
      font-size: 1.125rem;
      font-weight: bold;
    }

    &__options {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }

    &__option {
      display: flex;
      align-items: center;
      gap: 6px;
      min-height: 2.75rem;
      margin: 0;
      padding: 6px 12px;
      border: 2px solid var(--color-help-panel-button-border);
      border-radius: 8px;
      font-weight: normal;
      cursor: pointer;

      &:hover {
        background-color: color-mix(in srgb, var(--color-help-panel-on) 15%, transparent);
      }

      // two-tone, as on the panel's buttons
      &:focus-within {
        outline: 2px solid var(--color-help-panel-on);
        box-shadow: 0 0 0 4px var(--color-help-panel-focus);
      }

      &_selected,
      &_selected:hover {
        background-color: var(--color-help-panel-on);
        color: var(--color-help-panel-bg);
        font-weight: bold;
      }
    }

    &__icon {
      font-size: 1.25em;
    }

    &__sample {
      line-height: 1;
      font-weight: bold;
    }
  }
</style>
